/* The meta snapshot, and the deck advice derived from it.
 *
 * `data/meta.json` is generated by scripts/fetch-meta.mjs from published
 * tournament lists. The advice below is the same analysis as scripts/suggest.mjs
 * — one implementation's worth of maths, deliberately mirrored so the CLI and
 * the Advisor tab can never disagree.
 */

import { getCards } from './cards.js';
import { DECK_SIZE, totalCards } from './deck.js';

let snapshot = null;

export function getMeta() { return snapshot; }

export async function loadMeta(url = './data/meta.json') {
  if (snapshot) return snapshot;
  try {
    const r = await fetch(url);
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    snapshot = await r.json();
  } catch (err) {
    // The builder and board work fine without it; only the Meta tab needs it.
    snapshot = { generated: null, format: null, archetypes: [], error: String(err.message || err) };
  }
  return snapshot;
}

export const archetypes = () => snapshot?.archetypes || [];
export const archetypeFor = leaderId => archetypes().find(a => a.leader === leaderId) || null;
export const archetypeById = id => archetypes().find(a => String(a.id) === String(id)) || null;

/** A meta archetype as a loadable deck. */
export function archetypeDeck(a, which = 'consensus') {
  if (!a) return null;
  /* An archetype can reach the snapshot without a top list — every list in the
   * sample can fail the crawl, or be rejected as off-leader. Asking for 'top'
   * then threw and took the whole Meta view down with it, so fall back to the
   * consensus rather than failing. */
  const top = which === 'top' ? a.topList : null;
  const cards = top ? top.cards : a.consensus;
  if (!cards) return null;
  return {
    name: `${a.name} — ${top ? 'top list' : 'consensus'}`,
    leader: top ? top.leader : a.leader,
    cards: { ...cards },
  };
}

/* ---------------------------------------------------------------------- */
/* advice                                                                  */
/* ---------------------------------------------------------------------- */

/**
 * Diff a deck against its archetype's consensus 50.
 *
 * Because the consensus is exactly 50 cards, a legal deck's additions and cuts
 * balance — the result is a swap package rather than two unrelated wish-lists.
 *
 * @returns {null|{archetype, adds, cuts, flex, addN, cutN, balanced}}
 */
export function advise(deck, archetype) {
  const a = archetype || archetypeFor(deck.leader);
  if (!a) return null;

  const db = getCards();
  const stat = Object.fromEntries(a.cards.map(c => [c.id, c]));
  /* Share of lists running at least n copies. Missing from the table means the
   * field never goes that high, which is itself the finding. */
  const rate = (id, n) => (n <= 0 ? 1 : stat[id]?.atLeast?.[n] ?? 0);

  const ids = new Set([...Object.keys(deck.cards), ...Object.keys(a.consensus)]);
  const rows = [...ids].map(id => {
    const have = deck.cards[id] || 0;
    const want = a.consensus[id] || 0;
    const s = stat[id];
    return {
      id, have, want,
      delta: want - have,
      card: db[id] || null,
      name: db[id]?.name || id,
      yourShare: rate(id, have),
      wantShare: rate(id, want),
      isCore: (a.core?.[id] || 0) >= want && want > 0,
      include: s?.include ?? 0,
      avg: s?.avg ?? 0,
      modal: s?.modal ?? 0,
    };
  });

  const adds = rows.filter(r => r.delta > 0)
    .sort((x, y) => y.wantShare - x.wantShare || y.delta - x.delta);
  const cuts = rows.filter(r => r.delta < 0)
    .sort((x, y) => x.yourShare - y.yourShare || y.have - x.have);

  // Flex candidates the deck isn't already running at that depth.
  const seen = new Set();
  const flex = (a.contenders || []).filter(c => {
    if (seen.has(c.id) || (deck.cards[c.id] || 0) >= c.copy) return false;
    seen.add(c.id);
    return true;
  }).map(c => ({ ...c, card: db[c.id] || null, name: db[c.id]?.name || c.id }));

  const addN = adds.reduce((s, r) => s + r.delta, 0);
  const cutN = cuts.reduce((s, r) => s - r.delta, 0);

  return {
    archetype: a,
    adds, cuts, flex,
    addN, cutN,
    balanced: addN === cutN,
    total: totalCards(deck),
    deckSize: DECK_SIZE,
  };
}

/** Plain-English justification for one advice row. */
export function reasonFor(row) {
  if (row.delta > 0) {
    return `${fmtPct(row.wantShare)} of lists run ${row.want}+`;
  }
  if (row.include === 0) return 'no list in this sample runs it';
  if (row.want === 0 && row.include >= 0.3) {
    return `split — ${fmtPct(row.include)} run it (usually ${row.modal}), consensus spends the slot elsewhere`;
  }
  if (row.yourShare === 0) {
    return `nobody runs ${row.have}; ${fmtPct(row.include)} run it at all, usually ${row.modal}`;
  }
  return `only ${fmtPct(row.yourShare)} of lists run ${row.have}+ (field average ${fmtAvg(row.avg)})`;
}

export const fmtPct = v => `${(v * 100).toFixed(1)}%`;
/* A mean over a handful of lists is a repeating decimal more often than not,
 * and "field average 2.7333333333333334" reads as a bug in the advisor rather
 * than a number. Rounded at the display boundary, so the arithmetic upstream
 * keeps its precision. */
export const fmtAvg = v => Number(v || 0).toFixed(1);

/* ---------------------------------------------------------------------- */
/* structural checks — these apply to any deck, meta or brew               */
/* ---------------------------------------------------------------------- */

export function structuralNotes(stats) {
  const out = [];
  const add = (level, title, detail) => out.push({ level, title, detail });

  if (stats.counterCards < 18) {
    add('bad', 'Not enough counters',
      `${stats.counterCards} cards with a counter value. Competitive lists sit at 20–28; below 18 you lose races you should win.`);
  } else if (stats.counterCards > 32) {
    add('warn', 'Counter-heavy',
      `${stats.counterCards} counter cards. Defensive, but check you still have enough threats to close.`);
  } else {
    add('ok', 'Counter density',
      `${stats.counterCards} counter cards, ${stats.counterPower.toLocaleString()} total counter power.`);
  }

  if (stats.blockers === 0) add('warn', 'No blockers', 'Nothing to soak an attack. Fine for pure aggro, risky otherwise.');
  else add('ok', 'Blockers', `${stats.blockers} blocker${stats.blockers === 1 ? '' : 's'}.`);

  if (stats.triggers < 6) add('warn', 'Few triggers', `${stats.triggers} cards with a [Trigger]. Your life cards do nothing when flipped.`);
  else add('ok', 'Triggers', `${stats.triggers} trigger cards.`);

  if (stats.removal < 6) add('warn', 'Light on removal', `${stats.removal} cards that K.O. or shrink something. Top decks deploy more than one body a turn.`);
  else add('ok', 'Removal', `${stats.removal} interaction cards.`);

  const cheap = stats.curve[0] + stats.curve[1] + stats.curve[2];
  const top = stats.curve.slice(7).reduce((a, b) => a + b, 0);
  if (cheap < 8) add('warn', 'Top-heavy curve', `Only ${cheap} cards at cost 2 or less — expect dead early turns.`);
  if (top > 10) add('warn', 'Very top-heavy', `${top} cards at cost 7+. That is a lot you cannot cast before turn 4.`);
  add('ok', 'Curve', `Average cost ${stats.avgCost.toFixed(2)}.`);

  return out;
}
